import { enUS, jaJP, zhTW } from '@clerk/localizations';
import type { LocalizationResource } from '@clerk/shared/types';

/**
 * Maps the app's i18next language codes (see src/i18n/index.ts: 'en' | 'zh-TW'
 * | 'ja') to Clerk's prebuilt localization objects. Used to feed
 * `<ClerkProvider localization={...}>` so Clerk's prebuilt components render in
 * the app's selected language.
 *
 * Note: only `enUS` is officially maintained by Clerk; `zhTW` and `jaJP` are
 * community contributions, so some Clerk strings may remain in English.
 *
 * Version coupling: `@clerk/localizations` pulls its own `@clerk/shared`, which
 * may differ from the copy `@clerk/clerk-react` uses. `LocalizationResource` is
 * imported from `@clerk/shared/types` (the SDK's copy) and the exported locale
 * objects are plain data, so they compose today. When bumping either package,
 * re-run `bunx tsc --noEmit` to confirm the `LocalizationResource` types still
 * line up.
 */
const LOCALIZATIONS: Record<string, LocalizationResource> = {
    en: enUS,
    'zh-TW': zhTW,
    ja: jaJP,
};

/**
 * Returns the Clerk localization for an app language code, falling back to
 * English (`enUS`) for any unrecognized code.
 */
export function getClerkLocalization(lng: string): LocalizationResource {
    return LOCALIZATIONS[lng] ?? enUS;
}
